import { AggregationType, getDefaultMetrics } from 'features/admin/lib/metrics';

import React, {useMemo, useState} from 'react';
import cn from 'classnames';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title,
    Tooltip, Legend, TimeScale } from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';

import { useGetDashboardMetricsQuery } from 'features/admin/api/metricsAPI';
import { useDateRange } from 'shared/lib/hooks/useDateRange';
import {useChartData} from 'features/admin/model/useChartData';
import {getChartOptions} from 'features/admin/lib/getChartOptions';

import MetricSelector from '../MetricSelector/MetricSelector';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import AggregationSelector from '../AggregationSelector/AggregationSelector';

import styles from './MetricsDashboard.module.scss';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale, zoomPlugin);

const MetricsDashboard: React.FC = () => {
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>(getDefaultMetrics);
    const [aggregation, setAggregation] = useState<AggregationType>('day');

    const { customRange, dateRange, updateRange, updateCustomRange, selectedRange } = useDateRange();

    const { data: metricsData = [], isLoading, error, refetch } = useGetDashboardMetricsQuery({
        dateFrom: dateRange.start,
        dateTo: dateRange.end,
    });

    const hasDataToShow = metricsData.length > 0 && selectedMetrics.length > 0;

    const chartData = useChartData({
        metricsData,
        aggregation,
        dateRange,
        selectedMetrics,
    });

    const chartOptions = useMemo(() => {
        return getChartOptions(metricsData, aggregation);
    }, [metricsData, aggregation]);

    const handleMetricToggle = (metricId: string) => {
        setSelectedMetrics(prev =>
            prev.includes(metricId)
                ? prev.filter(id => id !== metricId)
                : [...prev, metricId],
        );
    };

    const handleSelectGroup = (groupMetrics: string[]) => {
        setSelectedMetrics(prev => {
            const newMetrics = [...prev];
            groupMetrics.forEach(metric => {
                if (!newMetrics.includes(metric)) newMetrics.push(metric);
            });
            return newMetrics;
        });
    };

    const handleDeselectGroup = (groupMetrics: string[]) => {
        setSelectedMetrics(prev => prev.filter(metric => !groupMetrics.includes(metric)));
    };

    if (error) {
        return (
            <div className={styles.error}>
                <h3>Ошибка загрузки данных</h3>
                <p>Не удалось загрузить метрики</p>
                <button onClick={() => refetch()} className={styles.retryButton}>
                    Повторить попытку
                </button>
            </div>
        );
    }

    if (isLoading) return <div className={styles.loading}>Загрузка данных...</div>;

    return (
        <div className={styles.dashboard}>
            <div className={styles.controls}>
                <div className={styles.controlRow}>
                    <DateRangePicker
                        onRangeChange={updateRange}
                        customRange={customRange}
                        onCustomRangeChange={(range) => updateCustomRange(range.start, range.end)}
                        currentRange={selectedRange}
                    />
                    <div className={cn(styles.controlRow, styles.detailsBlock)}>
                        <AggregationSelector
                            value={aggregation}
                            onChange={setAggregation}
                        />
                    </div>
                </div>
            </div>

            <div className={styles.chartBlock}>
                <div className={styles.chartContainer}>
                    {!hasDataToShow && (
                        <div className={styles.noDataMessage}>
                            {metricsData.length === 0
                                ? 'Нет данных за выбранный период'
                                : 'Выберите метрики для отображения'
                            }
                        </div>
                    )}
                    <div className={cn(styles.chartWrapper, {
                        [styles.withScroll]: metricsData.length > 50,
                    })}>
                        <div className={styles.chartCanvasContainer}>
                            <Line data={chartData} options={chartOptions} />
                        </div>
                    </div>
                </div>
                <div className={styles.metrics}>
                    <MetricSelector
                        selectedMetrics={selectedMetrics}
                        onMetricToggle={handleMetricToggle}
                        onSelectGroup={handleSelectGroup}
                        onDeselectGroup={handleDeselectGroup}
                    />
                </div>
            </div>
        </div>
    );
};

export default MetricsDashboard;